/**
 * matterStatus — PRESENTATION-ONLY mirror of the backend canonical matter-status domain.
 *
 * The backend (apps/api/src/lib/matter-status.ts) is the SOLE owner of matter lifecycle
 * BUSINESS classification. This mirror exists only for presentation concerns the UI needs
 * locally: status labels, badge styling, and tab filtering by open / terminal / archived.
 *
 * HARD BOUNDARY (enforced by matterStatus.test.ts): every function here takes a single
 * `status` string and nothing else. Anything that needs more than a status value — a win rate
 * (needs a set of matters), matter health (needs dates / tasks / hearings), or a hearing
 * outcome → status decision (a business policy) — is a BUSINESS rule. It lives ONLY in the
 * backend and reaches the UI as a pre-computed fact. Nothing here decides what a status MEANS;
 * it only decides how a status is DISPLAYED.
 *
 * The status buckets are pinned to the backend contract by matterStatus.test.ts so they can
 * never silently drift (the bug the audit found: `startsWith('closed')` dropped withdrawn /
 * discontinued / archived).
 */

export type MatterStatus =
  | 'intake'
  | 'active'
  | 'on_hold'
  | 'listed'
  | 'in_trial'
  | 'judgment_reserved'
  | 'closed_won'
  | 'closed_lost'
  | 'closed_settled'
  | 'withdrawn'
  | 'discontinued'
  | 'archived';

export const MATTER_STATUSES: readonly MatterStatus[] = [
  'intake',
  'active',
  'on_hold',
  'listed',
  'in_trial',
  'judgment_reserved',
  'closed_won',
  'closed_lost',
  'closed_settled',
  'withdrawn',
  'discontinued',
  'archived',
];

/** Terminal — the matter has ended (every closed_* plus withdrawn, discontinued, archived). */
export const TERMINAL_STATUSES: readonly MatterStatus[] = [
  'closed_won',
  'closed_lost',
  'closed_settled',
  'withdrawn',
  'discontinued',
  'archived',
];

/** Open — still live work. The exact complement of TERMINAL_STATUSES. */
export const OPEN_STATUSES: readonly MatterStatus[] = [
  'intake',
  'active',
  'on_hold',
  'listed',
  'in_trial',
  'judgment_reserved',
];

/** Archived — terminal AND hidden from active views (a sub-bucket of terminal). */
export const ARCHIVED_STATUSES: readonly MatterStatus[] = ['archived'];

/**
 * CLOSING — the statuses that END a matter through the close ceremony: terminal, minus
 * archived (which has its own flow and its own button).
 *
 * Picking one of these is not an edit, it is a closure: it completes the open tasks, vacates
 * the future hearings, cancels their diary entries and writes the closure snapshot. So an Edit
 * form that offers them hands over to the Close matter dialog, which states the consequences
 * first and records WHY — rather than doing all of that on a Save with nothing recorded.
 */
export const CLOSING_STATUSES: readonly MatterStatus[] = [
  'closed_won',
  'closed_lost',
  'closed_settled',
  'withdrawn',
  'discontinued',
];

const TERMINAL_SET = new Set<string>(TERMINAL_STATUSES);
const CLOSING_SET = new Set<string>(CLOSING_STATUSES);
const OPEN_SET = new Set<string>(OPEN_STATUSES);
const ARCHIVED_SET = new Set<string>(ARCHIVED_STATUSES);

/** Has the matter ended? Use for "Closed" tab filtering. */
export const isTerminalMatterStatus = (status: string | null | undefined): boolean =>
  status != null && TERMINAL_SET.has(status);

/** Does picking this status close the matter? Then it belongs to the close flow, not an edit. */
export const isClosingMatterStatus = (status: string | null | undefined): boolean =>
  status != null && CLOSING_SET.has(status);

/** Is the matter still live work? Use for "Active" tab filtering. */
export const isOpenMatterStatus = (status: string | null | undefined): boolean =>
  status != null && OPEN_SET.has(status);

/** Is the matter archived (terminal + hidden)? Use for the "Archived" tab. */
export const isArchivedMatterStatus = (status: string | null | undefined): boolean =>
  status != null && ARCHIVED_SET.has(status);

// ─── Presentation (labels + badge styling) ─────────────────────────────────
interface StatusPresentation {
  label: string;
  badgeClass: string;
}

const NEUTRAL_TERMINAL = 'bg-slate-50 text-slate-400 border border-slate-200';

const PRESENTATION: Record<MatterStatus, StatusPresentation> = {
  intake: { label: 'Intake', badgeClass: 'bg-slate-50 text-slate-500 border border-slate-200' },
  active: { label: 'Active', badgeClass: 'bg-emerald-50 text-emerald-700 dark:text-emerald-300 border border-emerald-200/70' },
  on_hold: { label: 'On Hold', badgeClass: 'bg-amber-50 text-amber-700 dark:text-amber-300 border border-amber-200/70' },
  listed: { label: 'Listed', badgeClass: 'bg-amber-50 text-amber-800 dark:text-amber-200 border border-amber-300/80' },
  in_trial: { label: 'In Trial', badgeClass: 'bg-orange-50 text-orange-800 dark:text-orange-200 border border-orange-200/70' },
  judgment_reserved: { label: 'Judgment Reserved', badgeClass: 'bg-violet-50 text-violet-700 dark:text-violet-300 border border-violet-200/70' },
  closed_won: { label: 'Closed · Won', badgeClass: 'bg-slate-50 text-slate-500 border border-slate-200' },
  closed_lost: { label: 'Closed', badgeClass: NEUTRAL_TERMINAL },
  closed_settled: { label: 'Settled', badgeClass: NEUTRAL_TERMINAL },
  withdrawn: { label: 'Withdrawn', badgeClass: NEUTRAL_TERMINAL },
  discontinued: { label: 'Discontinued', badgeClass: NEUTRAL_TERMINAL },
  archived: { label: 'Archived', badgeClass: NEUTRAL_TERMINAL },
};

/** Human label for a matter status (falls back to a de-underscored raw value). */
export function matterStatusLabel(status: string): string {
  return PRESENTATION[status as MatterStatus]?.label ?? status.replace(/_/g, ' ');
}

/** Badge classes for a matter status (falls back to a neutral pill). */
export function matterStatusBadgeClass(status: string): string {
  return PRESENTATION[status as MatterStatus]?.badgeClass ?? 'bg-slate-50 text-slate-600 dark:text-slate-400 border border-slate-200';
}
